import { AppBar, Box, Stack, Toolbar, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { IconButtonOutlined } from "../v2/utils/IconButtonOutlined";
import { ReactComponent as IconBack } from "../../assets/IconArrowLeft.svg";
import { useContext, useEffect, useRef, useState } from "react";
import { TempEventContext } from "../../context/TempEventContext";
import { CourseStructureCtx, TempEventCtx, UserCtx } from "../../interfaces";
import { Lti1p3QuestionSelector, QuestionButton } from "./Lti1p3QuestionSelector";
import { Course, Module, Question, Target, TargetSkill } from "../utils/CourseTree";
import RemediationReminder from "../v2/RemediationReminder";
import { CourseMetadata } from "@backend-types";
import { QuestionType, Role } from "../../enums";
import { generateGtmEvent, HandleDataLayer } from "../../Helpers";
import { OverflowTypography } from "../v2/utils/OverflowTypography";
import { Helmet } from "react-helmet-async";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import axios from "../../services/axios";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    height: 54,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxSizing: "border-box",
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingLeft: 32,
    paddingRight: 32,
    minHeight: "unset !important",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 86,
      paddingRight: 86,
    },
  },
  moduleTitle: {
    boxSizing: "border-box",
    paddingLeft: "24px",
    paddingRight: "24px",
    color: theme.palette.neutral.dark,
    flexGrow: 1,
    position: "relative",
    display: "flex",
    alignItems: "center",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  infoIcon: {
    color: "#0288D1",
  },
  logo: {
    color: theme.palette.text.primary,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 0.18,
    marginRight: 48,
  },
  manillaTab: {
    backgroundColor: theme.palette.primary.main,
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    height: "52px",
    width: "159px",
    padding: "15px 0px 15px 8px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    position: "fixed",
    zIndex: 1000,
    overflow: "hidden",
  },
  manillaBackButton: {
    color: "#fff",
    fontSize: "19px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "22px",
    letterSpacing: "-0.63px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  manillaButton: {
    fontSize: "19px",
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#19191914",
    },
    "&:active": {
      backgroundColor: "#19191929",
    },
  },
  manillaDragger: {
    fontSize: "24px",
    color: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "14px 16px 14px 8px",
    "&:hover": {
      backgroundColor: "#007CBF",
    },
    "&:active": {
      backgroundColor: "#016CA6",
    },
  },
  fullscreenOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0)",
    cursor: "grabbing",
    zIndex: 2000,
  },
  iconButton: {
    cursor: "pointer",
    boxSizing: "border-box",
    width: "38px",
    height: "38px",
    fontSize: "1.125rem",
    "&:hover": {
      transform: "scale(1.1)",
      backgroundColor: "rgba(25, 25, 25, 0.04)",
    },
  },
  studentViewButton: {
    paddingLeft: "16px",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
}));

interface Lti1p3TopNavProps {
  // functional
  allTargetQuestions: Question[];
  currentQuestion: Question;
  skillVideos: TargetSkill[];
  isSolverLoaded: boolean;
  questionFeedback?: string;
  userCtx: UserCtx;
  userCtxRef: React.MutableRefObject<UserCtx>;
  courseStructureCtx: CourseStructureCtx;
  courseStructureCtxRef: React.MutableRefObject<CourseStructureCtx>;
  // visual
  variant?: "floating" | "bar";
  showStudentModeBanner?: boolean;
  useResourceLinkTitle?: boolean;
  showTitle?: boolean;
  handleGoBack: () => void;
  handleMoveForward: (
    goToProblem: Question | null,
    suppressEoT?: boolean,
    suppressEoM?: boolean
  ) => void;
  handleOpenSkillModal: () => void;
  floatingOptions?: {
    /** width and height of the button in pixels */
    size: number;
    /** width and height of the icon in pixels */
    iconSize: number;
    /** position of the button on screen */
    position?: {
      /** absolute position from top in pixels */
      top: number;
      /** absolute position from left in pixels */
      left: number;
    };
  };
  remediationReminder: boolean;
  remediationReminderDot: boolean;
  handleHideRemediationReminder: () => void;
  handleNavigateAway: (callBack: () => void) => void;
  metadata?: CourseMetadata;
}

export const Lti1p3TopNav = (props: Lti1p3TopNavProps) => {
  const {
    showTitle = false,
    // showStudentModeBanner = false, // always disabled now
    useResourceLinkTitle = false,
    handleGoBack,
    handleMoveForward,
    handleNavigateAway,
    handleOpenSkillModal,
    handleHideRemediationReminder,
    variant = "bar",
    floatingOptions = {
      size: 40,
      iconSize: 16,
      position: {
        top: 14,
        left: 23,
      },
    },
    allTargetQuestions,
    currentQuestion,
    skillVideos,
    isSolverLoaded,
    userCtx,
    userCtxRef,
    courseStructureCtx,
    courseStructureCtxRef,
    remediationReminder,
    remediationReminderDot,
    metadata,
  } = props;
  const history = useHistory();
  const showStudentModeBanner = false;

  const showMaintBanner = userCtx.maintenanceWindow !== undefined;

  const classes = useStyles();
  const tempEventCtx = useContext(TempEventContext) as TempEventCtx;
  const [collapsed, setCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [collapsedPosition, setCollapsedPosition] = useState(0);
  const manillaRef = useRef<HTMLDivElement>(null);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const handleCollapse = () => {
    setCollapsed(true);
    generateGtmEvent({
      event: "collapse",
      action: "close",
      name: "onclick",
      type: "click",
      region: "navbar",
      section: "main navbar",
      text: "reduce header chevron",
    });
  };

  const handleExpand = () => {
    setCollapsed(false);
    generateGtmEvent({
      event: "collapse",
      action: "open",
      name: "onclick",
      type: "click",
      region: "navbar",
      section: "main navbar",
      text: "display header chevron",
    });
  };

  const handleStartDrag = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    setStartX(event.clientX); // Record the initial mouse X position
    setDragOffset(collapsedPosition); // Record the initial position of the tab
  };
  const handleEndDrag = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleDragging = (event: MouseEvent) => {
      if (isDragging && manillaRef.current) {
        const currentX = event.clientX;
        const diffX = currentX - startX;
        // Calculate new position
        let newCollapsedPosition = dragOffset - diffX;

        // Constrain new position to prevent it from going beyond the bounds
        const parentWidth = manillaRef.current?.parentElement?.offsetWidth ?? 0;
        const manillaWidth = manillaRef.current.offsetWidth;
        const leftBound = 0; // Adjust if there is an offset on the left side
        const rightBound = parentWidth - manillaWidth;

        // Applying the constraints
        if (newCollapsedPosition < leftBound) {
          newCollapsedPosition = leftBound;
        } else if (newCollapsedPosition > rightBound) {
          newCollapsedPosition = rightBound;
        }

        setCollapsedPosition(newCollapsedPosition);
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleDragging);
      document.addEventListener("mouseup", handleEndDrag);
    }

    return () => {
      document.removeEventListener("mousemove", handleDragging);
      document.removeEventListener("mouseup", handleEndDrag);
    };
  }, [isDragging, startX, dragOffset]);

  // resources button logic
  const gppQuestion = allTargetQuestions.find(
    (item) => item.type === QuestionType.guided
  );
  const showSkillVideos = skillVideos.length > 0;
  const showGppButton =
    showSkillVideos ||
    gppQuestion ||
    !metadata?.help_and_resource_button?.disable_guided_tour;
  const gppIsSelected =
    gppQuestion?.id !== undefined && currentQuestion.id === gppQuestion?.id;

  const currentTarget = currentQuestion?.parent as Target | undefined;
  const currentModule = currentTarget?.parent as Module | undefined;
  const currentCourse = currentModule?.parent as Course | undefined;

  const currentModuleName = currentModule?.getFormattedModuleName();
  const currentTargetName = currentTarget?.getFormattedTargetName();
  const questionTitle = `Module ${currentModule?.order ?? 1}.${
    currentTarget?.order ?? 1
  }: ${currentTargetName ?? ""}`;

  const floatingTitle = showTitle ? currentTargetName ?? "" : questionTitle.split(":")[0];

  const studentViewButton = [Role.teacher, Role.admin, Role.super].includes(
    userCtx.dbUser?.role
  ) ? (
    <Box className={classes.studentViewButton}>
      <IconButtonOutlined
        className={classes.iconButton}
        color="flush"
        onClick={async () => {
          try {
            const fullUrl = window.location.href;
            MacsAuthenticator.authHook.setExitStudentViewPath(fullUrl);

            const res = await axios.post(`/user/start-student-view`, null, {
              params: {
                launchedCourseId: currentCourse?.id,
                ltik: MacsAuthenticator.authHook.ltik,
              },
            });

            if (res.status === 200) {
              const newClassId = res.data?.newClassId;
              const launchedAssignmentId = res.data?.launchedAssignmentId;
              if (launchedAssignmentId)
                MacsAuthenticator.authHook.setLaunchedAssignmentId(launchedAssignmentId);
              MacsAuthenticator.authHook.setIsStudentView("true");
              //Set the role on the frontend temporarily
              userCtx.dbUser.role = Role.student;
              history.push({
                pathname: "/solver",
                search: new URLSearchParams({
                  classId: newClassId,
                  questionId: currentQuestion.id,
                }).toString(),
              });
              window.location.reload();
            }
          } catch (e: any) {
            console.error("Failed to start student view", e);
            throw e;
          }
        }}
        zoom={true}
        tooltipLabel="View as student"
        tooltipPlacement="bottom">
        <i
          className="fa-regular fa-graduation-cap"
          style={{
            color: "#747474",
            fontWeight: 900,
            fontSize: "20px",
          }}
        />
      </IconButtonOutlined>
    </Box>
  ) : (
    <></>
  );

  return (
    <>
      <Helmet>
        <title>
          {`${currentModuleName ?? ""} | ${currentTargetName ?? ""} | Digit`}
          {/* {variant === "floating" ? floatingTitle : questionTitle} | Digit */}
        </title>
      </Helmet>
      {isDragging && (
        <div className={classes.fullscreenOverlay} onMouseUp={handleEndDrag}></div>
      )}
      <AppBar position="static" sx={{ position: "relative" }}>
        {variant === "bar" && !collapsed && (
          <Toolbar disableGutters className={classes.root} tabIndex={-1}>
            <Stack
              sx={{
                position: "relative",
                flexGrow: 1,
                flexBasis: "50%",
                boxSizing: "border-box",
              }}
              direction="row"
              alignItems="center">
              <IconButtonOutlined
                className={classes.iconButton}
                sx={{ marginRight: "5px" }}
                color="flush"
                onClick={() => {
                  handleGoBack();
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "navbar",
                    section: "main navbar",
                    text: "back button",
                    component: currentModuleName,
                  });
                }}
                zoom={true}
                tooltipLabel="Back"
                tooltipPlacement="bottom">
                <IconBack style={{ width: "16px", height: "18px" }} />
              </IconButtonOutlined>
              {showTitle && (
                <Box className={classes.moduleTitle}>
                  <OverflowTypography
                    variant="h6"
                    fontWeight="600"
                    alignment="left"
                    height="24px"
                    component="span">
                    {questionTitle}
                  </OverflowTypography>
                </Box>
              )}
              {studentViewButton}
            </Stack>
            <Stack
              sx={{ position: "relative", flexGrow: 0 }}
              direction="row"
              alignItems="center">
              <Stack sx={{ position: "relative" }} direction="row" alignItems="center">
                {showGppButton && (
                  <Box
                    component="span"
                    key="guided-practice-button"
                    sx={{ marginRight: "20px" }}>
                    <QuestionButton
                      question={gppQuestion}
                      selected={gppIsSelected}
                      index={-1}
                      showSkillVideos={showSkillVideos}
                      handleButtonClick={(text?: string) => {
                        if (!gppQuestion) return;
                        // TODO need to change the data layer text
                        const buttonText = text || "guided practice button";
                        HandleDataLayer(
                          "link",
                          "click",
                          "internal link",
                          "main content",
                          "study resources",
                          buttonText,
                          "onclick",
                          currentModuleName ?? ""
                        );
                        handleNavigateAway(() =>
                          handleMoveForward(gppQuestion, true, true)
                        );
                      }}
                      handleOpenSkillModal={handleOpenSkillModal}
                      isSolverLoaded={isSolverLoaded}
                      userCtx={userCtx}
                      userCtxRef={userCtxRef}
                      remediationReminderDot={remediationReminderDot}
                      handleHideRemediationReminder={handleHideRemediationReminder}
                      metadata={metadata}
                      currentModule={currentModule}
                    />
                  </Box>
                )}
                <Lti1p3QuestionSelector
                  showGppButton={showGppButton ? true : false}
                  handleMoveForward={handleMoveForward}
                  handleNavigateAway={handleNavigateAway}
                  allTargetQuestions={allTargetQuestions}
                  currentQuestion={currentQuestion}
                  showSkillVideos={showSkillVideos}
                  handleOpenSkillModal={handleOpenSkillModal}
                  isSolverLoaded={isSolverLoaded}
                  userCtx={userCtx}
                  userCtxRef={userCtxRef}
                  courseStructureCtx={courseStructureCtx}
                  courseStructureCtxRef={courseStructureCtxRef}
                  remediationReminder={remediationReminder}
                  remediationReminderDot={remediationReminderDot}
                  handleHideRemediationReminder={handleHideRemediationReminder}
                  metadata={metadata}
                  currentModule={currentModule}
                />
                {remediationReminder && <RemediationReminder />}
              </Stack>
            </Stack>
            {/* <Typography
              variant="h4"
              component="h1"
              noWrap
              sx={{
                display: { xs: "none", sm: "flex" },
              }}
              className={classes.logo}>
              Digit
            </Typography> */}
            <Stack
              sx={{
                position: "relative",
                flexGrow: 1,
                flexBasis: "50%",
                boxSizing: "border-box",
              }}
              direction="row"
              alignItems="center">
              <Box sx={{ marginLeft: "auto" }}>
                <IconButtonOutlined
                  className={classes.iconButton}
                  color="flush"
                  zoom={true}
                  onClick={handleCollapse}
                  tooltipLabel="Reduce"
                  tooltipPlacement="bottom">
                  <i className="fa-regular fa-chevron-up"></i>
                </IconButtonOutlined>
              </Box>
            </Stack>
          </Toolbar>
        )}
        {variant === "bar" && collapsed && (
          <>
            <Box
              className={classes.manillaTab}
              onMouseUp={handleEndDrag}
              ref={manillaRef}
              sx={{
                top:
                  showStudentModeBanner && !tempEventCtx.dismissedStudentModeBanner
                    ? "48px"
                    : "0px",
                right: `${collapsedPosition}px`,
              }}>
              <Typography
                variant="h5"
                component="div"
                className={classes.manillaBackButton}>
                <Tooltip title="Back" placement="bottom">
                  <Box
                    className={classes.manillaButton}
                    onClick={(e) => {
                      handleGoBack();
                      generateGtmEvent({
                        event: "link",
                        action: "click",
                        name: "onclick",
                        type: "internal link",
                        region: "navbar",
                        section: "main navbar",
                        text: "back button",
                      });
                    }}>
                    <i className="fa-solid fa-arrow-left"></i>
                  </Box>
                </Tooltip>
                Back
              </Typography>
              <Tooltip title="Expand" placement="bottom">
                <Box onClick={handleExpand} className={classes.manillaButton}>
                  <i className="fa-solid fa-chevron-down"></i>
                </Box>
              </Tooltip>
              <Box
                className={classes.manillaDragger}
                sx={{ fontSize: "24px", cursor: isDragging ? "grabbing" : "grab" }}
                onMouseDown={handleStartDrag}>
                <i className="fa-regular fa-grip-dots-vertical"></i>
              </Box>
            </Box>
          </>
        )}
      </AppBar>

      {variant === "floating" && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 10,
            left: `${floatingOptions.position?.left ?? 0}px`,
            top: `${
              (floatingOptions.position?.top ?? 0) +
              (showStudentModeBanner && !tempEventCtx.dismissedStudentModeBanner
                ? 48
                : 0) +
              (showMaintBanner ? 60 : 0)
            }px`,
          }}>
          <Stack
            sx={{
              position: "relative",
              flexGrow: 1,
              flexBasis: "50%",
              boxSizing: "border-box",
            }}
            direction="row"
            alignItems="center">
            <IconButtonOutlined
              className={classes.iconButton}
              sx={{ marginRight: "5px" }}
              color="flush"
              onClick={handleGoBack}
              zoom={true}
              tooltipLabel="Back"
              tooltipPlacement="bottom">
              <IconBack style={{ width: "16px", height: "18px" }} />
            </IconButtonOutlined>
            {
              <Box className={classes.moduleTitle}>
                <Typography
                  style={{ whiteSpace: "nowrap", fontWeight: 700 }}
                  variant="h6"
                  fontSize={20}
                  fontWeight="600">
                  {useResourceLinkTitle &&
                  currentQuestion.name &&
                  currentQuestion.name.trim().toUpperCase() !== "N/A"
                    ? currentQuestion.name ?? floatingTitle
                    : floatingTitle}
                </Typography>
              </Box>
            }
            {studentViewButton}
          </Stack>
        </Box>
      )}
    </>
  );
};
